import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Trash2,
  AlertTriangle,
  Save,
  FlaskConical,
  Users,
  Truck,
  Banknote,
  UserPlus,
  Loader2,
  Printer,
  RefreshCw,
  Pencil,
  Info,
  ChevronRight,
  X,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
  LabelList,
} from "recharts";
import type { CashflowEvent } from "@/features/enterprises/types";
import { PageLoader, Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatAmount } from "@/utils/formatAmount";
import { CashflowChart } from "@/features/finances/components/CashflowChart";
import { SimGroupOverlayChart } from "@/features/finances/components/SimGroupOverlayChart";
import { SimulationDiscussion } from "@/features/finances/components/SimulationDiscussion";
import { SimulationAttachments } from "@/features/finances/components/SimulationAttachments";
import { printSimulationPDF } from "@/features/finances/utils/printSimulationPDF";
import {
  useSimulations,
  useSimulation,
  useSimulationPreview,
  useCreateSimulation,
  useUpdateSimulation,
  useDeleteSimulation,
} from "@/features/finances/hooks/useSimulations";
import { useGroupForecast } from "@/features/finances/hooks/useGroupForecast";
import {
  useGroupCashflow,
  useLatestBalances,
} from "@/features/group_finance/hooks/useGroupFinance";
import { simulationsApi } from "@/features/finances/api/simulations";
import { simulationDiscussionApi } from "@/features/finances/api/simulationDiscussion";
import type {
  OrderSimulationInput,
  SimulationAttachment,
  SimulationComment,
  SimulationResult,
  SimStaffResult,
} from "@/features/finances/types/simulation";

const STAFF_COLORS = [
  "#2563eb",
  "#16a34a",
  "#d97706",
  "#9333ea",
  "#dc2626",
  "#0891b2",
  "#ca8a04",
  "#db2777",
];
const MONTHS = [
  "sty",
  "lut",
  "mar",
  "kwi",
  "maj",
  "cze",
  "lip",
  "sie",
  "wrz",
  "paź",
  "lis",
  "gru",
];
const numInp =
  "px-2.5 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 transition";
const txtInp =
  "px-2.5 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 transition";

let _uidSeq = 0;
const uid = (): string => {
  try {
    return (globalThis.crypto as Crypto)?.randomUUID?.() ?? `u${++_uidSeq}`;
  } catch {
    return `u${++_uidSeq}`;
  }
};
const pad2 = (n: number) => String(n).padStart(2, "0");
const mk = (y: number, m: number) => `${y}-${m}`;
const shortMonth = (y: number, m: number) =>
  `${MONTHS[m - 1]} ${String(y).slice(2)}`;
const ymOf = (iso: string): { year: number; month: number } | null => {
  if (!iso) return null;
  const [y, m] = iso.split("-").map(Number);
  return y && m ? { year: y, month: m } : null;
};
const addMonths = (y: number, m: number, n: number) => {
  let yy = y,
    mm = m + n;
  while (mm > 12) {
    mm -= 12;
    yy++;
  }
  while (mm < 1) {
    mm += 12;
    yy--;
  }
  return { year: yy, month: mm };
};
const monthsBetween = (sy: number, sm: number, ey: number, em: number) =>
  Math.max(1, (ey - sy) * 12 + (em - sm) + 1);

type SimSource = "manual" | "group" | "tender" | "ze";
type Mo = { year: number; month: number; key: string };
function monthsOf(sy: number, sm: number, horizon: number): Mo[] {
  const out: Mo[] = [];
  let y = sy,
    m = sm;
  for (let i = 0; i < Math.max(1, Math.min(60, horizon)); i++) {
    out.push({ year: y, month: m, key: mk(y, m) });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return out;
}

interface StaffSlot {
  uid: string;
  is_new: boolean;
  employee: string | null;
  rate_pattern_employee: string | null;
  slot_label: string;
  frac: Record<string, number>;
}
interface ItemDraft {
  uid: string;
  name: string;
  qty: string;
  rate: string;
}
interface PayDraft {
  uid: string;
  date: string;
  pct: number;
}
interface ReqDraft {
  uid: string;
  desc: string;
  deadline: string;
  items: ItemDraft[];
  payments: PayDraft[];
}
interface RevDraft {
  uid: string;
  date: string;
  amount: number;
}
interface Variant {
  id: string;
  name: string;
  order_value: number;
  end_date: string;
  slots: StaffSlot[];
  reqs: ReqDraft[];
  revenue: RevDraft[];
}
interface SimDraft {
  name: string;
  start_year: number;
  start_month: number;
  overhead_rate_override: number | null;
  group: string | null;
  source: SimSource;
  linked_ref: string | null;
  linked_label: string;
  variants: Variant[];
  active: number;
}

function emptyVariant(name: string): Variant {
  const now = new Date();
  const e = addMonths(now.getFullYear(), now.getMonth() + 1, 11);
  return {
    id: uid(),
    name,
    order_value: 0,
    end_date: `${e.year}-${pad2(e.month)}-15`,
    slots: [],
    reqs: [],
    revenue: [],
  };
}
function emptyDraft(seed?: Partial<SimDraft>): SimDraft {
  const now = new Date();
  return {
    name: "Nowa symulacja",
    start_year: now.getFullYear(),
    start_month: now.getMonth() + 1,
    overhead_rate_override: null,
    group: null,
    source: "manual",
    linked_ref: null,
    linked_label: "",
    variants: [emptyVariant("Wariant A")],
    active: 0,
    ...seed,
  };
}

const reqTotal = (r: ReqDraft) =>
  r.items.reduce(
    (s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0),
    0,
  );
const paySum = (r: ReqDraft) =>
  r.payments.reduce((s, p) => s + (p.pct || 0), 0);

function fromLoaded(
  loaded: NonNullable<ReturnType<typeof useSimulation>["data"]>,
): SimDraft {
  const parseVariant = (
    v: {
      id?: string;
      name?: string;
      order_value?: number;
      end_date?: string;
      slots?: {
        is_new: boolean;
        employee: string | null;
        rate_pattern_employee: string | null;
        slot_label: string;
        frac: Record<string, number>;
      }[];
      reqs?: {
        desc: string;
        deadline: string | null;
        items: { name: string; qty: string; rate: string }[];
        payments: { date: string; pct: number }[];
      }[];
      revenue?: { date: string; amount: number }[];
    },
    idx: number,
  ): Variant => ({
    id: uid(),
    name: v.name || `Wariant ${String.fromCharCode(65 + idx)}`,
    order_value: v.order_value || 0,
    end_date: v.end_date || "",
    slots: (v.slots || []).map((s) => ({
      uid: uid(),
      is_new: s.is_new,
      employee: s.employee,
      rate_pattern_employee: s.rate_pattern_employee,
      slot_label: s.slot_label,
      frac: s.frac || {},
    })),
    reqs: (v.reqs || []).map((r) => ({
      uid: uid(),
      desc: r.desc || "",
      deadline: r.deadline || "",
      items: (r.items || []).map((it) => ({
        uid: uid(),
        name: it.name,
        qty: it.qty,
        rate: it.rate,
      })),
      payments: (r.payments || []).map((p) => ({
        uid: uid(),
        date: p.date,
        pct: p.pct,
      })),
    })),
    revenue: (v.revenue || []).map((t) => ({
      uid: uid(),
      date: t.date,
      amount: t.amount,
    })),
  });
  let variants: Variant[];
  if (loaded.variants && loaded.variants.length) {
    variants = (loaded.variants as Parameters<typeof parseVariant>[0][]).map(
      parseVariant,
    );
  } else {
    // legacy: zawiń pojedynczy zestaw w jeden wariant
    const slotsMap = new Map<string, StaffSlot>();
    for (const sm of loaded.staff_months) {
      const k = sm.is_new ? `n:${sm.slot_label}` : `e:${sm.employee}`;
      let slot = slotsMap.get(k);
      if (!slot) {
        slot = {
          uid: uid(),
          is_new: sm.is_new,
          employee: sm.employee,
          rate_pattern_employee: sm.rate_pattern_employee,
          slot_label: sm.slot_label,
          frac: {},
        };
        slotsMap.set(k, slot);
      }
      slot.frac[mk(sm.year, sm.month)] = Math.round(
        (parseFloat(sm.fraction) || 0) * 100,
      );
    }
    const reqs: ReqDraft[] = (loaded.contractor_reqs || []).map((r) => ({
      uid: uid(),
      desc: r.desc || "",
      deadline: r.deadline || "",
      items: (r.items || []).map((it) => ({
        uid: uid(),
        name: it.name,
        qty: it.quantity,
        rate: it.unit_rate,
      })),
      payments: (r.payments || []).map((p) => ({
        uid: uid(),
        date: p.date,
        pct: p.pct,
      })),
    }));
    const horizon = loaded.horizon_months || 12;
    const e = addMonths(loaded.start_year, loaded.start_month, horizon - 1);
    variants = [
      {
        id: uid(),
        name: "Wariant A",
        order_value: parseFloat(loaded.order_value || "0") || 0,
        end_date: `${e.year}-${pad2(e.month)}-15`,
        slots: [...slotsMap.values()],
        reqs,
        revenue: loaded.revenue_tranches.map((t) => ({
          uid: uid(),
          date: `${t.year}-${pad2(t.month)}-15`,
          amount: parseFloat(t.amount) || 0,
        })),
      },
    ];
  }
  return {
    name: loaded.name,
    start_year: loaded.start_year,
    start_month: loaded.start_month,
    overhead_rate_override:
      loaded.overhead_rate_override != null
        ? parseFloat(loaded.overhead_rate_override)
        : null,
    group: loaded.group,
    source: loaded.source,
    linked_ref: loaded.linked_ref,
    linked_label: loaded.linked_label,
    variants,
    active: 0,
  };
}

function planMonths(
  d: SimDraft,
  v: Variant,
): { months: Mo[]; endYM: { year: number; month: number }; horizon: number } {
  const endYM = ymOf(v.end_date) || addMonths(d.start_year, d.start_month, 11);
  const planEnd = addMonths(endYM.year, endYM.month, 1); // termin + 1 mc (ostatnie wypłaty w kolejnym)
  const horizon = monthsBetween(
    d.start_year,
    d.start_month,
    planEnd.year,
    planEnd.month,
  );
  return {
    months: monthsOf(d.start_year, d.start_month, horizon),
    endYM,
    horizon,
  };
}

// contractor_lines z zapotrzebowań: każda płatność (data + %) + automat domknięcia do 100% na terminie
function deriveContractorLines(reqs: ReqDraft[]) {
  const lines: {
    name: string;
    quantity: string;
    unit_rate: string;
    pay_year: number;
    pay_month: number;
  }[] = [];
  for (const r of reqs) {
    const T = reqTotal(r);
    if (T <= 0) continue;
    const pays = [...r.payments.filter((p) => p.date && p.pct > 0)];
    const rem = 100 - pays.reduce((s, p) => s + p.pct, 0);
    if (rem > 0.001 && r.deadline)
      pays.push({ uid: "final", date: r.deadline, pct: rem });
    for (const p of pays) {
      const ym = ymOf(p.date);
      if (!ym) continue;
      lines.push({
        name: r.desc || "Podwykonawca",
        quantity: "1",
        unit_rate: ((T * p.pct) / 100).toFixed(2),
        pay_year: ym.year,
        pay_month: ym.month,
      });
    }
  }
  return lines;
}
function deriveRevenue(v: Variant, endYM: { year: number; month: number }) {
  const tr: { amount: string; year: number; month: number }[] = [];
  let partial = 0;
  for (const t of v.revenue) {
    const ym = ymOf(t.date);
    if (!ym || !(t.amount > 0)) continue;
    tr.push({ amount: t.amount.toFixed(2), year: ym.year, month: ym.month });
    partial += t.amount;
  }
  const rem = v.order_value - partial; // automat: reszta na terminie (zawsze)
  if (rem > 0.005)
    tr.push({ amount: rem.toFixed(2), year: endYM.year, month: endYM.month });
  return tr;
}
function previewPayload(d: SimDraft, v: Variant): OrderSimulationInput {
  const { months, endYM, horizon } = planMonths(d, v);
  const staff_months = v.slots.flatMap((slot) =>
    months
      .filter((mm) => (slot.frac[mm.key] || 0) > 0)
      .map((mm) => ({
        employee: slot.is_new ? null : slot.employee,
        rate_pattern_employee: slot.is_new ? slot.rate_pattern_employee : null,
        slot_label: slot.slot_label,
        is_new: slot.is_new,
        year: mm.year,
        month: mm.month,
        fraction: ((slot.frac[mm.key] || 0) / 100).toFixed(3),
      })),
  );
  return {
    name: d.name,
    start_year: d.start_year,
    start_month: d.start_month,
    horizon_months: horizon,
    order_value: v.order_value.toFixed(2),
    // Wyślij jawny null (nie undefined) — przy PATCH undefined znika z JSON-a, więc powrót
    // z trybu ręcznego na „auto" nie wyczyściłby zapisanego override (zostałby ręczny po F5).
    overhead_rate_override:
      d.overhead_rate_override != null
        ? d.overhead_rate_override.toFixed(4)
        : null,
    staff_months,
    contractor_lines: deriveContractorLines(v.reqs),
    revenue_tranches: deriveRevenue(v, endYM),
  };
}
function variantJSON(v: Variant) {
  return {
    id: v.id,
    name: v.name,
    order_value: v.order_value,
    end_date: v.end_date,
    slots: v.slots.map((s) => ({
      is_new: s.is_new,
      employee: s.employee,
      rate_pattern_employee: s.rate_pattern_employee,
      slot_label: s.slot_label,
      frac: s.frac,
    })),
    reqs: v.reqs.map((r) => ({
      desc: r.desc,
      deadline: r.deadline || null,
      items: r.items.map((it) => ({
        name: it.name,
        qty: it.qty,
        rate: it.rate,
      })),
      payments: r.payments
        .filter((p) => p.date)
        .map((p) => ({ date: p.date, pct: p.pct })),
    })),
    revenue: v.revenue
      .filter((t) => t.date)
      .map((t) => ({ date: t.date, amount: t.amount })),
  };
}
function savePayload(d: SimDraft): OrderSimulationInput {
  const v = d.variants[d.active];
  const base = previewPayload(d, v);
  // variants (JSON) to źródło prawdy przy ponownym wczytaniu; relacje (staff/contractor/revenue)
  // = aktywny wariant (dla akcji compute)
  return {
    ...base,
    group: d.group,
    source: d.source,
    linked_ref: d.linked_ref,
    linked_label: d.linked_label,
    variants: d.variants.map(variantJSON),
  };
}

export function OrderSimulationPage() {
  const { data: sims, isLoading } = useSimulations();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const { data: loaded, isLoading: simLoading } = useSimulation(
    creating ? null : selectedId,
  );
  const [draft, setDraft] = useState<SimDraft>(emptyDraft());
  const create = useCreateSimulation();
  const update = useUpdateSimulation();
  const del = useDeleteSimulation();
  const preview = useSimulationPreview();
  const [results, setResults] = useState<
    Record<string, SimulationResult | null>
  >({});
  const [showOverhead, setShowOverhead] = useState(false);
  const [showGroupOverlay, setShowGroupOverlay] = useState(false);
  const [renaming, setRenaming] = useState<{ i: number; name: string } | null>(
    null,
  );
  const v = draft.variants[draft.active] ?? draft.variants[0];
  const { months, endYM, horizon } = planMonths(draft, v);

  // obłożenie istniejących pracowników z bazy (per miesiąc) dla planu — baseline „zajęte"
  const { data: forecastWin } = useGroupForecast(
    draft.start_year,
    draft.start_month,
    Math.min(24, horizon),
  );
  const committed = useMemo(() => {
    const m: Record<string, Record<string, number>> = {};
    for (const e of forecastWin?.employees ?? []) {
      m[e.employee_id] = {};
      for (const c of e.by_month)
        m[e.employee_id][mk(c.year, c.month)] = Math.round(
          c.total_fraction * 100,
        );
    }
    return m;
  }, [forecastWin]);

  const now = new Date();
  const { data: forecastEmp } = useGroupForecast(
    now.getFullYear(),
    now.getMonth() + 1,
    1,
  );
  const employees = forecastEmp?.employees ?? [];

  // Dane GK do nałożenia cashflow w PDF (te same źródła co SimGroupOverlayChart).
  const { data: groupEvents = [] } = useGroupCashflow();
  const { data: groupBalances = [] } = useLatestBalances();
  const groupInitial = useMemo(
    () =>
      groupBalances.reduce(
        (s, b) => s + (parseFloat(b.net_balance ?? "0") || 0),
        0,
      ),
    [groupBalances],
  );

  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const src = params.get("source");
    if (src && !creating && !selectedId) {
      setCreating(true);
      setDraft(
        emptyDraft({
          name: params.get("name") || "Symulacja",
          source: src as SimSource,
          group: params.get("group"),
          linked_ref: params.get("ref"),
          linked_label: params.get("label") || "",
        }),
      );
      ["source", "group", "name", "ref", "label"].forEach((p) =>
        params.delete(p),
      );
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  // Deep-link z powiadomienia o oznaczeniu: ?sim=<id> otwiera daną symulację.
  useEffect(() => {
    const sim = params.get("sim");
    if (sim) {
      setCreating(false);
      setSelectedId(sim);
      params.delete("sim");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  useEffect(() => {
    if (creating) setDraft((d) => (params.get("source") ? d : emptyDraft()));
    else if (loaded)
      setDraft(fromLoaded(loaded)); /* eslint-disable-next-line */
  }, [creating, loaded]);

  // live preview aktywnego wariantu
  const activePayload = useMemo(() => previewPayload(draft, v), [draft, v]);
  useEffect(() => {
    const t = setTimeout(
      () =>
        preview.mutate(activePayload, {
          onSuccess: (r) => setResults((p) => ({ ...p, [v.id]: r })),
        }),
      400,
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePayload]);
  const live = results[v.id] ?? null;

  async function previewAll(): Promise<
    Record<string, SimulationResult | null>
  > {
    // Liczy WSZYSTKIE warianty i zwraca świeżą mapę wyników — nie polegaj na stanie
    // `results` w domknięciu (setResults jest asynchroniczne; do druku potrzebujemy
    // gwarancji, że każdy wariant ma policzony wynik).
    const out: Record<string, SimulationResult | null> = {};
    for (const vv of draft.variants) {
      try {
        out[vv.id] = await simulationsApi.preview(previewPayload(draft, vv));
      } catch {
        out[vv.id] = null;
      }
    }
    setResults((p) => ({ ...p, ...out }));
    return out;
  }

  // edycja aktywnego wariantu
  const setV = (patch: Partial<Variant>) =>
    setDraft((d) => {
      const vs = d.variants.slice();
      vs[d.active] = { ...vs[d.active], ...patch };
      return { ...d, variants: vs };
    });

  function validate(): string | null {
    for (const slot of v.slots) {
      if (!Object.values(slot.frac).some((x) => x > 0)) continue;
      if (slot.is_new && !slot.rate_pattern_employee)
        return "Nowy etat musi mieć wzorzec stawki (jak…).";
      if (!slot.is_new && !slot.employee)
        return "Przydział istniejącego musi wskazywać pracownika.";
    }
    return null;
  }
  async function save() {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }
    if (creating) {
      const c = await create.mutateAsync(savePayload(draft));
      setCreating(false);
      setSelectedId(c.id);
    } else if (selectedId)
      await update.mutateAsync({ id: selectedId, payload: savePayload(draft) });
  }

  function addVariant() {
    setDraft((d) => {
      const name = `Wariant ${String.fromCharCode(65 + d.variants.length)}`;
      const copy: Variant = {
        ...d.variants[d.active],
        id: uid(),
        name,
        slots: d.variants[d.active].slots.map((s) => ({
          ...s,
          uid: uid(),
          frac: { ...s.frac },
        })),
        reqs: d.variants[d.active].reqs.map((r) => ({
          ...r,
          uid: uid(),
          items: r.items.map((it) => ({ ...it, uid: uid() })),
          payments: r.payments.map((p) => ({ ...p, uid: uid() })),
        })),
        revenue: d.variants[d.active].revenue.map((t) => ({
          ...t,
          uid: uid(),
        })),
      };
      return {
        ...d,
        variants: [...d.variants, copy],
        active: d.variants.length,
      };
    });
  }
  function rmVariant(i: number) {
    setDraft((d) => {
      if (d.variants.length <= 1) return d;
      const vs = d.variants.filter((_, j) => j !== i);
      return {
        ...d,
        variants: vs,
        active: Math.max(0, Math.min(d.active, vs.length - 1)),
      };
    });
  }
  function renameVariant(i: number) {
    setRenaming({ i, name: draft.variants[i].name });
  }
  function commitRename(name: string) {
    const trimmed = name.trim();
    setDraft((d) => {
      if (renaming == null || !trimmed) return d;
      const vs = d.variants.slice();
      vs[renaming.i] = { ...vs[renaming.i], name: trimmed };
      return { ...d, variants: vs };
    });
    setRenaming(null);
  }

  if (isLoading) return <PageLoader />;
  const saving = create.isPending || update.isPending;
  // domyślny % = efektywny UDZIAŁ OBŁOŻENIOWY zlecenia (auto z backendu); override nadpisuje udział
  const defOverheadPct = live
    ? parseFloat(live.overhead_basis.rate_effective) * 100
    : null;

  return (
    <div className="flex flex-col lg:flex-row gap-5">
      <aside className="lg:w-52 shrink-0 print:hidden">
        <button
          onClick={() => {
            setCreating(true);
            setSelectedId(null);
            setDraft(emptyDraft());
          }}
          className="w-full flex items-center justify-center gap-2 mb-3 px-3 py-2.5 rounded-lg bg-[var(--color-primary)] text-white text-sm font-semibold hover:opacity-90 shadow-sm transition"
        >
          <Plus size={16} /> Nowa symulacja
        </button>
        <ul className="flex flex-col gap-1.5">
          {(sims ?? []).map((s) => {
            const active = selectedId === s.id && !creating;
            return (
              <li key={s.id}>
                <button
                  onClick={() => {
                    setCreating(false);
                    setSelectedId(s.id);
                  }}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm border transition ${active ? "bg-white dark:bg-gray-800 border-[var(--color-primary)]/30 text-[var(--color-primary)] font-semibold shadow-sm" : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/60 text-gray-700 dark:text-gray-200 shadow-sm"}`}
                >
                  <span className="flex items-center gap-1.5">
                    <FlaskConical size={13} className="opacity-60 shrink-0" />{" "}
                    <span className="truncate">{s.name}</span>
                  </span>
                </button>
              </li>
            );
          })}
          {(sims ?? []).length === 0 && (
            <li className="text-xs text-gray-400 px-3 py-6 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              Brak zapisanych symulacji.
            </li>
          )}
        </ul>
      </aside>

      <div className="flex-1 min-w-0">
        {!creating && !selectedId ? (
          <div className="flex flex-col items-center justify-center text-center py-24 text-gray-400 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
            <FlaskConical size={44} className="mb-3 opacity-40" />
            <p className="text-sm max-w-sm">
              Symulacja NOWEGO zlecenia — koszt, marża i cashflow przed decyzją.
            </p>
          </div>
        ) : !creating && selectedId && simLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-gray-400">
            <Spinner size="lg" />
            <span className="text-sm">Wczytywanie danych symulacji…</span>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Nazwa + akcje */}
            <div className="flex items-center justify-between gap-3 print:hidden">
              <div className="flex items-center gap-2 min-w-0">
                <FlaskConical
                  size={18}
                  className="text-[var(--color-primary)] shrink-0"
                />
                <input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Nazwa symulacji"
                  className="text-xl font-bold bg-transparent border-b border-transparent hover:border-gray-200 focus:border-[var(--color-primary)] outline-none text-gray-900 dark:text-gray-100 transition min-w-0"
                />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => {
                    const computed = await previewAll();
                    // Dla zapisanej symulacji dociągnij dyskusję + załączniki do druku.
                    let comments: SimulationComment[] = [];
                    let attachments: SimulationAttachment[] = [];
                    if (!creating && selectedId) {
                      try {
                        [comments, attachments] = await Promise.all([
                          simulationDiscussionApi.listComments(selectedId),
                          simulationDiscussionApi.listAttachments(selectedId),
                        ]);
                      } catch {
                        /* druk wygeneruje się i bez nich */
                      }
                    }
                    printSimulationPDF(draft, computed, employees, {
                      comments,
                      attachments,
                      groupEvents,
                      groupInitial,
                    });
                  }}
                  title="Drukuj / PDF (A4 pion)"
                >
                  <Printer size={15} />
                </Button>
                <Button
                  onClick={save}
                  loading={saving}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Save size={16} className="mr-1.5" /> Zapisz
                </Button>
                {!creating && selectedId && (
                  <button
                    onClick={() => {
                      if (confirm("Usunąć symulację?")) {
                        del.mutate(selectedId);
                        setSelectedId(null);
                      }
                    }}
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                    title="Usuń"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Zakładki wariantów + porównanie marż */}
            <div className="flex flex-wrap items-center gap-2 print:hidden">
              {draft.variants.map((vv, i) => {
                const r = results[vv.id];
                const m = r ? parseFloat(r.margin) : null;
                const active = i === draft.active;
                return (
                  <div
                    key={vv.id}
                    className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 cursor-pointer transition ${active ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5" : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40"}`}
                    onClick={() => setDraft((d) => ({ ...d, active: i }))}
                  >
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                      {vv.name}
                    </span>
                    {m != null && (
                      <span
                        className={`text-xs tabular-nums ${m < 0 ? "text-red-600" : "text-green-600"}`}
                      >
                        {formatAmount(m.toFixed(2))}
                      </span>
                    )}
                    {active && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          renameVariant(i);
                        }}
                        className="text-gray-400 hover:text-gray-600 ml-1"
                      >
                        <Pencil size={12} />
                      </button>
                    )}
                    {active && draft.variants.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          rmVariant(i);
                        }}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                );
              })}
              <button
                onClick={addVariant}
                className="flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 px-2 py-1.5 rounded-lg"
              >
                <Plus size={14} /> wariant
              </button>
              <button
                onClick={previewAll}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5"
                title="Przelicz marże wszystkich wariantów"
              >
                <RefreshCw size={13} />
              </button>
            </div>

            {live && (
              <SimulationSummaryPanel
                name={draft.name}
                orderValue={v.order_value}
                result={live}
              />
            )}

            <div className="grid xl:grid-cols-[1fr_24rem] gap-5 items-start">
              {/* LEWA: edytory + (na dole) cashflow */}
              <div className="flex flex-col gap-5 min-w-0">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-sm text-gray-500">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-400">
                        Wartość zlecenia
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          step={1000}
                          value={v.order_value || ""}
                          onChange={(e) =>
                            setV({
                              order_value: Math.max(
                                0,
                                parseFloat(e.target.value) || 0,
                              ),
                            })
                          }
                          className={`${numInp} w-36`}
                        />
                        <span className="font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                          {formatAmount(v.order_value.toFixed(2))}{" "}
                          <span className="text-xs font-normal text-gray-400">
                            PLN
                          </span>
                        </span>
                      </div>
                    </div>
                    <label className="flex items-center gap-1.5">
                      Start
                      <input
                        type="number"
                        min={1}
                        max={12}
                        value={draft.start_month}
                        onChange={(e) =>
                          setDraft({ ...draft, start_month: +e.target.value })
                        }
                        className={`${numInp} w-14`}
                      />
                      /
                      <input
                        type="number"
                        value={draft.start_year}
                        onChange={(e) =>
                          setDraft({ ...draft, start_year: +e.target.value })
                        }
                        className={`${numInp} w-20`}
                      />
                    </label>
                    <label className="flex items-center gap-1.5">
                      Termin (koniec)
                      <DatePicker
                        value={v.end_date}
                        onChange={(val) => setV({ end_date: val })}
                      />
                    </label>
                    <label className="flex items-center gap-1.5">
                      Udział obciążenia (narzut)
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={
                          draft.overhead_rate_override != null
                            ? Math.round(draft.overhead_rate_override * 100)
                            : defOverheadPct != null
                              ? Math.round(defOverheadPct)
                              : ""
                        }
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            overhead_rate_override:
                              e.target.value === ""
                                ? null
                                : (parseFloat(e.target.value) || 0) / 100,
                          })
                        }
                        className={`${numInp} w-16`}
                      />
                      <span className="text-gray-400">%</span>
                      <button
                        onClick={() => setShowOverhead(true)}
                        className="text-gray-400 hover:text-[var(--color-primary)]"
                        title="Skąd ten %"
                      >
                        <Info size={14} />
                      </button>
                      {draft.overhead_rate_override != null && (
                        <button
                          onClick={() =>
                            setDraft({ ...draft, overhead_rate_override: null })
                          }
                          className="text-[10px] text-gray-400 hover:text-gray-600 underline"
                          title="Przywróć udział domyślny (auto)"
                        >
                          {defOverheadPct != null
                            ? `auto ${Math.round(defOverheadPct)}%`
                            : "auto"}
                        </button>
                      )}
                      <span className="text-[10px] text-gray-400">
                        {draft.overhead_rate_override != null
                          ? "przyjęty (ręczny)"
                          : "auto wg wartości w portfelu"}
                      </span>
                    </label>
                    <span className="text-xs text-gray-400">
                      Plan: {shortMonth(draft.start_year, draft.start_month)} –{" "}
                      {shortMonth(endYM.year, endYM.month)} (+1 mc na wypłaty)
                    </span>
                  </div>
                </div>

                <StaffEditor
                  v={v}
                  setV={setV}
                  employees={employees}
                  months={months}
                  committed={committed}
                />
                <ContractorReqEditor v={v} setV={setV} />
                <RevenueEditor v={v} setV={setV} endYM={endYM} />

                {/* Cashflow — na samym dole lewej kolumny (pełna szerokość) */}
                <div
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5"
                  id="sim-cashflow"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                      <Banknote
                        size={16}
                        className="text-[var(--color-primary)]"
                      />{" "}
                      Prognozowane przepływy pieniężne
                    </h3>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Nałóż pełny przepływ GK ELEKTIM
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={showGroupOverlay}
                        onClick={() => setShowGroupOverlay((s) => !s)}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-1 ${showGroupOverlay ? "bg-[var(--color-primary)]" : "bg-gray-200 dark:bg-gray-600"}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${showGroupOverlay ? "translate-x-4" : "translate-x-0"}`}
                        />
                      </button>
                    </label>
                  </div>
                  {live ? (
                    showGroupOverlay ? (
                      <SimGroupOverlayChart simEvents={live.cashflow_events} />
                    ) : (
                      <CashflowChart
                        events={live.cashflow_events}
                        defaultPreset="all"
                      />
                    )
                  ) : (
                    <p className="text-sm text-gray-400">
                      Dodaj dane, by zobaczyć cashflow.
                    </p>
                  )}
                </div>

                {/* S6 — pełny rozkład miesięczny: przychód vs koszty (płace/narzut/podwykonawcy) */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    <Banknote size={16} className="text-[var(--color-primary)]" /> Rozkład
                    miesięczny — przychód vs koszty
                  </h3>
                  {live ? (
                    <SimMonthlyBreakdownChart events={live.cashflow_events} />
                  ) : (
                    <p className="text-sm text-gray-400">Dodaj dane, by zobaczyć rozkład.</p>
                  )}
                  <p className="text-[11px] text-gray-400 mt-1">
                    Słupek w górę = przychód; w dół = płace, narzut (koszty stałe + nieprojektowi
                    wg udziału), podwykonawcy. Miesiąc po miesiącu.
                  </p>
                </div>

                {/* Załączniki + dyskusja — tylko dla zapisanej symulacji (potrzebują jej ID) */}
                {!creating && selectedId && (
                  <>
                    <SimulationAttachments simulationId={selectedId} />
                    <SimulationDiscussion simulationId={selectedId} />
                  </>
                )}
              </div>

              {/* PRAWA: KPI + obłożenie + ostrzeżenia (sticky) */}
              <div className="xl:sticky xl:top-4 print:hidden">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-mono uppercase tracking-wider text-gray-500">
                    Wynik — {v.name}
                  </h2>
                  {preview.isPending && (
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Loader2 size={12} className="animate-spin" /> przeliczam…
                    </span>
                  )}
                </div>
                {live ? (
                  <ResultPanel result={live} employees={employees} />
                ) : (
                  <div className="text-sm text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-4">
                    Dodaj ludzi / podwykonawców / przychód — policzy się sam.
                  </div>
                )}
              </div>
            </div>

            {showOverhead && live && (
              <OverheadModal
                basis={live.overhead_basis}
                usedRate={parseFloat(live.overhead_rate)}
                override={draft.overhead_rate_override}
                onClose={() => setShowOverhead(false)}
              />
            )}

            {renaming && (
              <RenameVariantModal
                initial={renaming.name}
                onCancel={() => setRenaming(null)}
                onSave={commitRename}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RenameVariantModal({
  initial,
  onCancel,
  onSave,
}: {
  initial: string;
  onCancel: () => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState(initial);
  const trimmed = name.trim();
  const submit = () => {
    if (trimmed) onSave(name);
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-[24rem] max-w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <span className="text-[var(--color-primary)]">
              <Pencil size={17} />
            </span>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Nazwa wariantu
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 shrink-0"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Krótka, czytelna etykieta — np. „Z podwykonawcą" albo „Własne siły".
        </p>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancel();
            }
          }}
          onFocus={(e) => e.target.select()}
          placeholder="Nazwa wariantu"
          className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 transition"
        />
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Anuluj
          </Button>
          <Button size="sm" onClick={submit} disabled={!trimmed}>
            <Save size={14} /> Zapisz
          </Button>
        </div>
      </div>
    </div>
  );
}

function OverheadModal({
  basis,
  usedRate,
  override,
  onClose,
}: {
  basis: SimulationResult["overhead_basis"];
  usedRate: number;
  override: number | null;
  onClose: () => void;
}) {
  const amt = (x: number) => `${formatAmount(x.toFixed(2))} PLN`;
  const rows = basis.rows ?? [];
  const totalPool = parseFloat(basis.total_pool) || 0;
  const fixedPart = parseFloat(basis.fixed_part) || 0;
  const peoplePart = parseFloat(basis.people_part) || 0;
  const totalNarzut = fixedPart + peoplePart;
  const [open, setOpen] = useState<string | null>(null);  // który miesiąc rozwinięty (rozbicie obciążenia)
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-[44rem] max-w-full max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Narzut kosztów stałych — wg OBCIĄŻENIA KOSZTOWEGO
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Narzut idzie za <b>obciążeniem kosztowym</b> — tak jak na realnych projektach. W każdym
              miesiącu udział zlecenia = jego obciążenie ÷ (obciążenie istniejącego portfela GK +
              obciążenie zlecenia), × pula (koszty stałe cykliczne + nieprojektowi). Im droższą obsadę
              realnie wiążesz na zleceniu, tym większy jego udział w kosztach firmy.
              <br />
              <span className="text-[11px] text-gray-400">
                <b>Obciążenie kosztowe</b> = Σ (% etatu × miesięczna pensja danej osoby), w zł — nie suchy
                etat. Osoba na 50% za 12&nbsp;000 zł waży 6&nbsp;000 zł; senior obciąża firmę bardziej niż
                junior na tym samym %. Dlatego narzut rozkłada się wg realnego ciężaru pensji, a nie liczby głów.
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {override != null && (
          <div className="mt-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
            <p className="flex items-center gap-1.5 text-xs font-bold text-amber-800 dark:text-amber-300">
              <AlertTriangle size={14} className="shrink-0" /> UWAGA: udział narzutu ustawiony ręcznie
            </p>
            <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1 leading-snug">
              Przyjęto płaski udział {(override * 100).toFixed(1)}% na wszystkie miesiące zamiast
              obciążeniowego. Narzut i marża <b>nie odzwierciedlają</b> realnego obciążenia — to ręczna korekta.
            </p>
          </div>
        )}

        <h3 className="text-[11px] font-mono uppercase tracking-wider text-gray-400 mt-4 mb-1">
          Wyliczenie miesiąc po miesiącu
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-400 text-left">
              <th className="py-1">Miesiąc</th>
              <th className="py-1 text-right">Obciążenie zlecenia</th>
              <th className="py-1 text-right">Portfel GK</th>
              <th className="py-1 text-right">Udział</th>
              <th className="py-1 text-right">Pula/mc</th>
              <th className="py-1 text-right">Narzut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const key = `${r.year}-${r.month}`;
              const isOpen = open === key;
              return (
              <Fragment key={key}>
              <tr
                onClick={() => setOpen(isOpen ? null : key)}
                className="text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                <td className="py-1.5 flex items-center gap-1">
                  <ChevronRight size={12} className={`text-gray-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  {MONTHS[r.month - 1]} {r.year}
                </td>
                <td className="py-1.5 text-right tabular-nums">{amt(r.sim_load)}</td>
                <td className="py-1.5 text-right tabular-nums text-gray-400">{amt(r.existing_load)}</td>
                <td className="py-1.5 text-right tabular-nums">{(parseFloat(r.share) * 100).toFixed(1)}%</td>
                <td className="py-1.5 text-right tabular-nums text-gray-400">{amt(parseFloat(r.pool))}</td>
                <td className="py-1.5 text-right tabular-nums">{amt(parseFloat(r.narzut))}</td>
              </tr>
              {isOpen && (
                <tr className="bg-gray-50/60 dark:bg-gray-700/20">
                  <td colSpan={6} className="py-2 px-3">
                    <div className="grid sm:grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                          Obciążenie zlecenia ({amt(r.sim_load)})
                        </div>
                        {r.sim_people.length ? (
                          <ul className="space-y-0.5">
                            {r.sim_people.map((p, i) => (
                              <li key={i} className="flex justify-between gap-2 text-gray-600 dark:text-gray-300">
                                <span>{p.name}</span>
                                <span className="tabular-nums text-gray-400 whitespace-nowrap">
                                  {(p.fraction * 100).toFixed(0)}% · {amt(parseFloat(p.cost))}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-400">Brak obsady w tym miesiącu.</p>
                        )}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                          Portfel GK ({amt(r.existing_load)})
                        </div>
                        {r.portfolio.length ? (
                          <ul className="space-y-0.5">
                            {r.portfolio.map((p, i) => (
                              <li key={i} className="flex justify-between gap-2 text-gray-600 dark:text-gray-300">
                                <span>{p.name}</span>
                                <span className="tabular-nums text-gray-400 whitespace-nowrap">{amt(p.load)}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-400">Brak innego obciążenia (zlecenie samo na rynku).</p>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
              );
            })}
            <tr className="font-semibold text-gray-900 dark:text-gray-100 border-t border-gray-300 dark:border-gray-600">
              <td className="py-1.5">Razem ({rows.length} mc)</td>
              <td></td>
              <td></td>
              <td className="py-1.5 text-right tabular-nums">{(usedRate * 100).toFixed(1)}% śr.</td>
              <td className="py-1.5 text-right tabular-nums">{amt(totalPool)}</td>
              <td className="py-1.5 text-right tabular-nums text-red-700">{amt(totalNarzut)}</td>
            </tr>
          </tbody>
        </table>

        <h3 className="text-[11px] font-mono uppercase tracking-wider text-gray-400 mt-4 mb-1">
          Rozbicie narzutu
        </h3>
        <table className="w-full text-sm">
          <tbody>
            <tr className="text-gray-600 dark:text-gray-300">
              <td className="py-1.5">Koszty stałe (proporcjonalne)</td>
              <td className="py-1.5 text-right tabular-nums">{amt(fixedPart)}</td>
            </tr>
            <tr className="text-gray-600 dark:text-gray-300">
              <td className="py-1.5">Nieprojektowi (proporcjonalni)</td>
              <td className="py-1.5 text-right tabular-nums">{amt(peoplePart)}</td>
            </tr>
            <tr className="font-semibold text-gray-900 dark:text-gray-100 border-t border-gray-200 dark:border-gray-700">
              <td className="py-1.5">Narzut razem</td>
              <td className="py-1.5 text-right tabular-nums">{amt(totalNarzut)}</td>
            </tr>
          </tbody>
        </table>

        <p className="text-[11px] text-gray-400 mt-3">
          Obciążenie kosztowe = Σ (% etatu × pensja) z prognozy/faktu (te same dane co wykresy obłożenia),
          nie suchy etat — droższa obsada waży więcej. Miesiąc bez obsady zlecenia → 0 narzutu. Im więcej
          (i droższej) równoległej pracy w GK, tym mniejszy udział tego zlecenia.
        </p>
      </div>
    </div>
  );
}

function Section({
  title,
  hint,
  icon,
  onAdd,
  addLabel,
  children,
}: {
  title: string;
  hint?: string;
  icon: React.ReactNode;
  onAdd?: () => void;
  addLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="mt-0.5 text-[var(--color-primary)] shrink-0">
            {icon}
          </span>
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h2>
            {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
          </div>
        </div>
        {onAdd && (
          <button
            onClick={onAdd}
            className="flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 px-2 py-1 rounded-md transition shrink-0"
          >
            <Plus size={14} /> {addLabel}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

// Pasek 0–100% (10% bloki) + opcjonalny baseline „zajęte" z bazy (szara kreska + etykieta)
function AllocStrip({
  months,
  frac,
  committed,
  onSet,
}: {
  months: Mo[];
  frac: Record<string, number>;
  committed?: Record<string, number>;
  onSet: (key: string, pct: number) => void;
}) {
  return (
    <div className="flex gap-1 overflow-x-auto">
      {months.map((mm) => {
        const pct = frac[mm.key] || 0;
        const g = Math.round(pct / 10);
        const occ = committed?.[mm.key] ?? 0;
        const click = (fromBottom: number) => {
          const cand = (fromBottom + 1) * 10;
          onSet(mm.key, cand === g * 10 ? fromBottom * 10 : cand);
        };
        const over = occ + pct > 100;
        return (
          <div
            key={mm.key}
            className="flex flex-col items-center gap-1 flex-1 min-w-[28px]"
          >
            <div className="relative w-full h-16 rounded-md overflow-hidden border border-gray-300 dark:border-gray-600 flex flex-col gap-px">
              {Array.from({ length: 10 }).map((_, idxTop) => {
                const fromBottom = 9 - idxTop;
                const filled = fromBottom < g;
                return (
                  <div
                    key={idxTop}
                    onClick={() => click(fromBottom)}
                    className={`flex-1 cursor-pointer hover:opacity-80 ${filled ? "bg-[var(--color-primary)]" : "bg-gray-100 dark:bg-gray-700"}`}
                  />
                );
              })}
              {occ > 0 && (
                <div
                  className="absolute left-0 right-0 border-t-2 border-dashed"
                  style={{
                    bottom: `${Math.min(100, occ)}%`,
                    borderColor: over ? "#dc2626" : "#9ca3af",
                  }}
                  title={`Zajęte poza tym przydziałem (baza + inne przydziały): ${occ}%`}
                />
              )}
            </div>
            <div className="flex flex-col items-center text-[9px] text-gray-400 leading-tight text-center w-full">
              <span className="whitespace-nowrap">
                {shortMonth(mm.year, mm.month)}
              </span>
              {pct > 0 && (
                <span className="whitespace-nowrap">{g * 10}%</span>
              )}
              {occ > 0 && (
                <span
                  className={`whitespace-nowrap ${over ? "text-red-500" : ""}`}
                >
                  zajęte {occ}%
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StaffEditor({
  v,
  setV,
  employees,
  months,
  committed,
}: {
  v: Variant;
  setV: (p: Partial<Variant>) => void;
  employees: { employee_id: string; employee_name: string }[];
  months: Mo[];
  committed: Record<string, Record<string, number>>;
}) {
  const addSlot = () =>
    setV({
      slots: [
        ...v.slots,
        {
          uid: uid(),
          is_new: false,
          employee: employees[0]?.employee_id ?? null,
          rate_pattern_employee: null,
          slot_label: "",
          frac: {},
        },
      ],
    });
  const updSlot = (i: number, patch: Partial<StaffSlot>) =>
    setV({ slots: v.slots.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const setFrac = (i: number, key: string, pct: number) =>
    setV({
      slots: v.slots.map((s, j) =>
        j === i ? { ...s, frac: { ...s.frac, [key]: pct } } : s,
      ),
    });

  // Suma przydziałów TEJ symulacji per istniejący pracownik / miesiąc (%) — łącznie ze wszystkich
  // slotów (ta sama osoba może być na zleceniu w kilku rolach). Podstawa warningu „>100%".
  const simByEmp: Record<string, Record<string, number>> = {};
  for (const s of v.slots) {
    if (s.is_new || !s.employee) continue;
    const acc = (simByEmp[s.employee] ??= {});
    for (const mm of months) {
      const p = s.frac[mm.key] || 0;
      if (p > 0) acc[mm.key] = (acc[mm.key] || 0) + p;
    }
  }
  // Baseline „zajęte poza tym przydziałem" dla paska slotu: zajętość w bazie (prognoza/fakt) +
  // przydziały tej osoby w INNYCH slotach symulacji. Pasek tego slotu dorzuca swój % na wierzch.
  const externalOccFor = (
    slot: StaffSlot,
  ): Record<string, number> | undefined => {
    if (slot.is_new || !slot.employee) return undefined;
    const db = committed[slot.employee] || {};
    const sim = simByEmp[slot.employee] || {};
    const out: Record<string, number> = {};
    for (const mm of months) {
      const others = Math.max(0, (sim[mm.key] || 0) - (slot.frac[mm.key] || 0));
      const base = (db[mm.key] || 0) + others;
      if (base > 0) out[mm.key] = base;
    }
    return out;
  };
  // Miesiące, w których łączna zajętość osoby (baza + CAŁA ta symulacja) przekracza 100% — tylko
  // tam, gdzie symulacja realnie ją obciąża. Ostrzegamy, ale NIE blokujemy dodania/zapisu.
  const overMonthsFor = (empId: string) =>
    months
      .map((mm) => ({
        mm,
        sim: simByEmp[empId]?.[mm.key] || 0,
        total:
          (committed[empId]?.[mm.key] || 0) + (simByEmp[empId]?.[mm.key] || 0),
      }))
      .filter((x) => x.sim > 0 && x.total > 100);

  return (
    <Section
      title="Ludzie na zleceniu"
      hint="Klik na pasku (krok 10%). Kreska = ile osoba ma już zajęte poza tym przydziałem (baza + inne przydziały). Czerwień = przekroczenie 100% (ostrzega, nie blokuje)."
      icon={<Users size={17} />}
      onAdd={addSlot}
      addLabel="Dodaj osobę"
    >
      {v.slots.length === 0 && (
        <p className="text-xs text-gray-400 py-3 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          Brak. Dodaj istniejącego pracownika lub nowy etat.
        </p>
      )}
      <div className="flex flex-col gap-3">
        {v.slots.map((slot, i) => (
          <div
            key={slot.uid}
            className="rounded-xl border border-gray-200 dark:border-gray-700 p-3"
          >
            <div className="flex flex-wrap items-center gap-2 mb-2 text-sm">
              <select
                value={slot.is_new ? "new" : "existing"}
                onChange={(e) =>
                  updSlot(
                    i,
                    e.target.value === "new"
                      ? { is_new: true, employee: null }
                      : {
                          is_new: false,
                          rate_pattern_employee: null,
                          slot_label: "",
                          employee:
                            slot.employee ?? employees[0]?.employee_id ?? null,
                        },
                  )
                }
                className={txtInp}
              >
                <option value="existing">Istniejący</option>
                <option value="new">Nowy etat</option>
              </select>
              {slot.is_new ? (
                <>
                  <span className="text-amber-600">
                    <UserPlus size={14} />
                  </span>
                  <input
                    placeholder="Etykieta (np. Nowy monter)"
                    value={slot.slot_label}
                    onChange={(e) => updSlot(i, { slot_label: e.target.value })}
                    className={`${txtInp} w-44`}
                  />
                  <select
                    value={slot.rate_pattern_employee ?? ""}
                    onChange={(e) =>
                      updSlot(i, {
                        rate_pattern_employee: e.target.value || null,
                      })
                    }
                    className={`${txtInp} ${!slot.rate_pattern_employee ? "border-amber-400 ring-1 ring-amber-200" : ""}`}
                  >
                    <option value="">stawka jak… (wymagane)</option>
                    {employees.map((em) => (
                      <option key={em.employee_id} value={em.employee_id}>
                        jak {em.employee_name}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <select
                  value={slot.employee ?? ""}
                  onChange={(e) =>
                    updSlot(i, { employee: e.target.value || null })
                  }
                  className={`${txtInp} ${!slot.employee ? "border-amber-400 ring-1 ring-amber-200" : ""}`}
                >
                  <option value="">— wybierz pracownika —</option>
                  {employees.map((em) => (
                    <option key={em.employee_id} value={em.employee_id}>
                      {em.employee_name}
                    </option>
                  ))}
                </select>
              )}
              <button
                onClick={() =>
                  setV({ slots: v.slots.filter((_, j) => j !== i) })
                }
                className="text-gray-300 hover:text-red-600 transition ml-auto"
              >
                <Trash2 size={15} />
              </button>
            </div>
            <AllocStrip
              months={months}
              frac={slot.frac}
              committed={externalOccFor(slot)}
              onSet={(key, pct) => setFrac(i, key, pct)}
            />
            {!slot.is_new &&
              slot.employee &&
              (() => {
                const over = overMonthsFor(slot.employee);
                if (over.length === 0) return null;
                return (
                  <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-red-300 dark:border-red-800/60 bg-red-50 dark:bg-red-900/20 px-2.5 py-1.5 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    <span>
                      <b>Przekroczenie 100%</b> (zajętość w bazie + ta
                      symulacja):{" "}
                      {over.map((o, k) => (
                        <span
                          key={o.mm.key}
                          className="whitespace-nowrap tabular-nums"
                        >
                          {k > 0 ? ", " : ""}
                          {shortMonth(o.mm.year, o.mm.month)} —{" "}
                          {Math.round(o.total)}%
                        </span>
                      ))}
                      . Można zapisać mimo to — rozważ dotrudnienie lub
                      przeskalowanie przydziałów.
                    </span>
                  </div>
                );
              })()}
          </div>
        ))}
      </div>
    </Section>
  );
}

function ContractorReqEditor({
  v,
  setV,
}: {
  v: Variant;
  setV: (p: Partial<Variant>) => void;
}) {
  const addReq = () =>
    setV({
      reqs: [
        ...v.reqs,
        {
          uid: uid(),
          desc: "",
          deadline: "",
          items: [{ uid: uid(), name: "", qty: "1", rate: "0" }],
          payments: [],
        },
      ],
    });
  const updReq = (i: number, patch: Partial<ReqDraft>) =>
    setV({ reqs: v.reqs.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  return (
    <Section
      title="Planowani podwykonawcy i pozostałe koszty jednorazowe"
      hint="Robocze zapotrzebowania: pozycje, wartości, termin, punkty płatności (data + %). Reszta domyka się na terminie."
      icon={<Truck size={17} />}
      onAdd={addReq}
      addLabel="Dodaj zapotrzebowanie"
    >
      {v.reqs.length === 0 && (
        <p className="text-xs text-gray-400 py-3 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          Brak. Dodaj robocze zapotrzebowanie.
        </p>
      )}
      <div className="flex flex-col gap-3">
        {v.reqs.map((r, i) => {
          const T = reqTotal(r);
          const explicitSum = paySum(r);
          const rem = Math.max(0, 100 - explicitSum);
          const setItems = (items: ItemDraft[]) => updReq(i, { items });
          const setPays = (payments: PayDraft[]) => updReq(i, { payments });
          return (
            <div
              key={r.uid}
              className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <input
                  placeholder="Opis (np. Roboty ziemne)"
                  value={r.desc}
                  onChange={(e) => updReq(i, { desc: e.target.value })}
                  className={`${txtInp} flex-1 min-w-[12rem]`}
                />
                <label className="flex items-center gap-1.5 text-xs text-gray-500">
                  termin
                  <DatePicker
                    value={r.deadline}
                    onChange={(val) => updReq(i, { deadline: val })}
                  />
                </label>
                <button
                  onClick={() =>
                    setV({ reqs: v.reqs.filter((_, j) => j !== i) })
                  }
                  className="text-gray-300 hover:text-red-600 transition"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <div>
                <div className="grid grid-cols-[1fr_5rem_7rem_7rem_2rem] gap-2 text-[10px] uppercase tracking-wider text-gray-400 px-1">
                  <span>Pozycja</span>
                  <span className="text-right">Ilość</span>
                  <span className="text-right">Stawka</span>
                  <span className="text-right">Wartość</span>
                  <span />
                </div>
                <div className="space-y-1.5 mt-1">
                  {r.items.map((it, j) => (
                    <div
                      key={it.uid}
                      className="grid grid-cols-[1fr_5rem_7rem_7rem_2rem] gap-2 items-center"
                    >
                      <input
                        value={it.name}
                        onChange={(e) =>
                          setItems(
                            r.items.map((x, k) =>
                              k === j ? { ...x, name: e.target.value } : x,
                            ),
                          )
                        }
                        placeholder="Nazwa"
                        className={txtInp}
                      />
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={it.qty}
                        onChange={(e) =>
                          setItems(
                            r.items.map((x, k) =>
                              k === j ? { ...x, qty: e.target.value } : x,
                            ),
                          )
                        }
                        className={numInp}
                      />
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={it.rate}
                        onChange={(e) =>
                          setItems(
                            r.items.map((x, k) =>
                              k === j ? { ...x, rate: e.target.value } : x,
                            ),
                          )
                        }
                        className={numInp}
                      />
                      <div className="text-right text-sm tabular-nums text-gray-700 dark:text-gray-200">
                        {formatAmount(
                          (
                            (parseFloat(it.qty) || 0) *
                            (parseFloat(it.rate) || 0)
                          ).toFixed(2),
                        )}
                      </div>
                      <button
                        onClick={() =>
                          setItems(r.items.filter((_, k) => k !== j))
                        }
                        disabled={r.items.length === 1}
                        className="text-gray-300 hover:text-red-600 disabled:opacity-30"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <button
                    onClick={() =>
                      setItems([
                        ...r.items,
                        { uid: uid(), name: "", qty: "1", rate: "0" },
                      ])
                    }
                    className="text-xs text-[var(--color-primary)] hover:underline flex items-center gap-1"
                  >
                    <Plus size={12} /> pozycja
                  </button>
                  <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                    Razem: {formatAmount(T.toFixed(2))} PLN
                  </span>
                </div>
              </div>
              <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
                <span className="text-[10px] uppercase tracking-wider text-gray-400">
                  Punkty płatności (data + %)
                </span>
                <div className="space-y-1.5 mt-1">
                  {r.payments.map((p, j) => (
                    <div
                      key={p.uid}
                      className="flex items-center gap-2 text-sm"
                    >
                      <DatePicker
                        value={p.date}
                        onChange={(val) =>
                          setPays(
                            r.payments.map((x, k) =>
                              k === j ? { ...x, date: val } : x,
                            ),
                          )
                        }
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={5}
                        value={p.pct}
                        onChange={(e) =>
                          setPays(
                            r.payments.map((x, k) =>
                              k === j
                                ? {
                                    ...x,
                                    pct: Math.max(
                                      0,
                                      Math.min(
                                        100,
                                        parseInt(e.target.value || "0", 10) ||
                                          0,
                                      ),
                                    ),
                                  }
                                : x,
                            ),
                          )
                        }
                        className={`${numInp} w-20`}
                      />
                      <span className="text-xs text-gray-400">%</span>
                      <span className="text-xs tabular-nums text-gray-500 ml-1">
                        = {formatAmount(((T * (p.pct || 0)) / 100).toFixed(2))}
                      </span>
                      <button
                        onClick={() =>
                          setPays(r.payments.filter((_, k) => k !== j))
                        }
                        className="text-gray-300 hover:text-red-600 ml-auto"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {/* automat: domknięcie 100% na terminie (szare, read-only) */}
                  {rem > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <span className="px-2.5 py-1.5 rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                        {r.deadline
                          ? new Date(r.deadline).toLocaleDateString("pl-PL")
                          : "termin"}
                      </span>
                      <span className="tabular-nums w-20 text-right">
                        {rem}%
                      </span>
                      <span className="text-xs">%</span>
                      <span className="text-xs tabular-nums ml-1">
                        = {formatAmount(((T * rem) / 100).toFixed(2))}
                      </span>
                      <span className="text-[10px] ml-auto italic">
                        domknięcie na terminie
                      </span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() =>
                    setPays([
                      ...r.payments,
                      { uid: uid(), date: r.deadline || "", pct: 0 },
                    ])
                  }
                  className="text-xs text-[var(--color-primary)] hover:underline flex items-center gap-1 mt-1.5"
                >
                  <Plus size={12} /> punkt płatności
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function RevenueEditor({
  v,
  setV,
  endYM,
}: {
  v: Variant;
  setV: (p: Partial<Variant>) => void;
  endYM: { year: number; month: number };
}) {
  const partialSum = v.revenue.reduce(
    (s, t) => s + (t.amount > 0 ? t.amount : 0),
    0,
  );
  const finalRem = Math.max(0, v.order_value - partialSum);
  return (
    <Section
      title="Wpływy — wartość zlecenia"
      hint="Częściówki: jakie kwoty i kiedy. Reszta domyka się na terminie."
      icon={<Banknote size={17} />}
      onAdd={() =>
        setV({ revenue: [...v.revenue, { uid: uid(), date: "", amount: 0 }] })
      }
      addLabel="Dodaj transzę"
    >
      {v.revenue.length === 0 && (
        <p className="text-xs text-gray-400 py-3 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          Brak częściówek. Resztę domknie automat na terminie.
        </p>
      )}
      <div className="flex flex-col gap-2">
        {v.revenue.map((t, i) => (
          <div
            key={t.uid}
            className="flex flex-wrap items-center gap-2 text-sm"
          >
            <DatePicker
              value={t.date}
              onChange={(val) =>
                setV({
                  revenue: v.revenue.map((x, j) =>
                    j === i ? { ...x, date: val } : x,
                  ),
                })
              }
            />
            <input
              type="number"
              min={0}
              step={1000}
              value={t.amount || ""}
              onChange={(e) =>
                setV({
                  revenue: v.revenue.map((x, j) =>
                    j === i
                      ? {
                          ...x,
                          amount: Math.max(0, parseFloat(e.target.value) || 0),
                        }
                      : x,
                  ),
                })
              }
              className={`${numInp} w-36`}
            />
            <span className="text-gray-400 text-xs">PLN</span>
            <button
              onClick={() =>
                setV({ revenue: v.revenue.filter((_, j) => j !== i) })
              }
              className="text-gray-300 hover:text-red-600 ml-auto"
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        {/* automat reszty na terminie — szary, read-only */}
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <span className="px-2.5 py-1.5 rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
            {shortMonth(endYM.year, endYM.month)}
          </span>
          <span className="tabular-nums w-36 text-right">
            {formatAmount(finalRem.toFixed(2))}
          </span>
          <span className="text-xs">PLN</span>
          <span className="text-[10px] ml-auto italic">
            domknięcie na terminie
          </span>
        </div>
      </div>
      <div className="mt-2 text-xs text-gray-500">
        Razem przychód:{" "}
        <b className="tabular-nums text-gray-900 dark:text-gray-100">
          {formatAmount((partialSum + finalRem).toFixed(2))} PLN
        </b>{" "}
        {v.order_value > 0 && (
          <span className="text-gray-400">
            (wartość zlecenia {formatAmount(v.order_value.toFixed(2))})
          </span>
        )}
      </div>
    </Section>
  );
}

// S6 — pełny rozkład miesięczny: przychód (w górę) vs koszty (w dół: płace / narzut / podwykonawcy).
function SimMonthlyBreakdownChart({ events }: { events: CashflowEvent[] }) {
  const data = useMemo(() => {
    const by = new Map<
      string,
      { label: string; y: number; m: number; revenue: number; salaries: number; narzut: number; contractors: number }
    >();
    for (const e of events) {
      const [y, m] = e.date.split("-").map(Number);
      if (!y || !m) continue;
      const key = `${y}-${m}`;
      let row = by.get(key);
      if (!row) {
        row = { label: `${MONTHS[m - 1]} ${String(y).slice(2)}`, y, m, revenue: 0, salaries: 0, narzut: 0, contractors: 0 };
        by.set(key, row);
      }
      const amt = parseFloat(e.amount) || 0;
      if (e.type === "planned_revenue") row.revenue += amt;
      else if (e.type === "salary_cost") row.salaries += amt; // amt < 0
      else if (e.type === "recurring_cost") row.narzut += amt; // amt < 0
      else if (e.type === "planned_contractor_cost") row.contractors += amt; // amt < 0
    }
    return [...by.values()].sort((a, b) => a.y - b.y || a.m - b.m);
  }, [events]);
  if (data.length === 0)
    return <p className="text-sm text-gray-400">Brak danych miesięcznych — dodaj przychód / ludzi / podwykonawców.</p>;
  const fmtY = (v: number) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }} stackOffset="sign">
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtY} />
        <Tooltip formatter={(v) => `${formatAmount(Math.abs(Number(v)).toFixed(2))} PLN`} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <ReferenceLine y={0} stroke="#9ca3af" />
        <Bar dataKey="revenue" name="Przychód" stackId="a" fill="#16a34a" radius={[2, 2, 0, 0]} />
        <Bar dataKey="salaries" name="Płace" stackId="a" fill="#2563eb" />
        <Bar dataKey="narzut" name="Narzut" stackId="a" fill="#f59e0b" />
        <Bar dataKey="contractors" name="Podwykonawcy" stackId="a" fill="#a855f7" radius={[0, 0, 2, 2]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function StaffSimChart({
  staff,
  nameFor,
}: {
  staff: SimStaffResult[];
  nameFor: (s: SimStaffResult) => string;
}) {
  const monthMap = new Map<string, { year: number; month: number }>();
  for (const s of staff)
    for (const m of s.by_month)
      monthMap.set(`${m.year}-${m.month}`, { year: m.year, month: m.month });
  const ms = [...monthMap.values()].sort(
    (a, b) => a.year - b.year || a.month - b.month,
  );
  const series = staff.map((s, i) => ({
    key: `s${i}`,
    name: nameFor(s),
    color: STAFF_COLORS[i % STAFF_COLORS.length],
    staff: s,
  }));
  const data: Record<string, number | string>[] = ms.map((mm) => {
    const row: Record<string, number | string> = {
      label: `${MONTHS[mm.month - 1]} ${String(mm.year).slice(2)}`,
    };
    for (const ser of series) {
      const c = ser.staff.by_month.find(
        (x) => x.year === mm.year && x.month === mm.month,
      );
      // wysokość słupka = KOSZT części etatu w tym miesiącu (PLN); % etatu obok, w nawiasie
      row[ser.key] = c ? parseFloat(c.cost) || 0 : 0;
      row[`${ser.key}_p`] = c ? Math.round(c.fraction * 100) : 0;
    }
    return row;
  });
  // liczba całkowita z separatorem tysięcy (bez waluty) — zwięzła etykieta kosztu na słupku
  const fmtInt = (v: number) =>
    Math.round(v)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  // etykieta segmentu: KWOTA + (gdy się mieści) % etatu w nawiasie; biały tekst na kolorze osoby
  const segLabel =
    (pctKey: string) =>
    (p: {
      x?: number | string;
      y?: number | string;
      width?: number | string;
      height?: number | string;
      value?: number | string | boolean | null;
      index?: number;
    }) => {
      const x = Number(p.x) || 0,
        y = Number(p.y) || 0,
        width = Number(p.width) || 0,
        height = Number(p.height) || 0;
      const cost = Number(p.value) || 0;
      if (cost <= 0 || height < 12 || width < 24) return null;
      const pct = Number(data[p.index ?? 0]?.[pctKey] ?? 0);
      const showPct = pct > 0 && height >= 24 && width >= 30;
      const cx = x + width / 2;
      const cy = y + height / 2;
      return (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          pointerEvents="none"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          <tspan
            x={cx}
            dy={showPct ? "-0.45em" : 0}
            fontSize={9}
            fontWeight={600}
          >
            {fmtInt(cost)}
          </tspan>
          {showPct && (
            <tspan x={cx} dy="1.15em" fontSize={8} opacity={0.95}>
              ({pct}%)
            </tspan>
          )}
        </text>
      );
    };
  return (
    <>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
        {series.map((ser) => (
          <span
            key={ser.key}
            className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300"
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: ser.color }}
            />{" "}
            {ser.name}
          </span>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={232}>
        <BarChart
          data={data}
          margin={{ top: 8, right: 8, left: -4, bottom: 4 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#e5e7eb"
            opacity={0.4}
          />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 9 }}
            interval={0}
            tickLine={false}
            angle={-40}
            textAnchor="end"
            height={38}
          />
          <YAxis
            width={40}
            tick={{ fontSize: 9 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(t) =>
              Math.abs(Number(t)) >= 1000
                ? `${Math.round(Number(t) / 1000)}k`
                : `${Math.round(Number(t))}`
            }
          />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            formatter={(value, key, item) => {
              const k = String(key);
              const pct = Number(
                (item?.payload as Record<string, number> | undefined)?.[
                  `${k}_p`
                ] ?? 0,
              );
              return [
                `${formatAmount(Number(value).toFixed(2))} PLN · ${pct}%`,
                series.find((s) => s.key === k)?.name ?? k,
              ];
            }}
          />
          {series.map((ser, idx) => (
            <Bar
              key={ser.key}
              dataKey={ser.key}
              stackId="a"
              fill={ser.color}
              name={ser.name}
              radius={idx === series.length - 1 ? [3, 3, 0, 0] : undefined}
            >
              <LabelList dataKey={ser.key} content={segLabel(`${ser.key}_p`)} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </>
  );
}

// Panel-nagłówek symulacji: po lewej nazwa + wartość zlecenia (kwota), po prawej
// ładny panel KPI (te same karty co w „Wynik"): przychód, koszty, marża netto,
// marża %. Widoczny u góry, od razu po zakładkach wariantów.
function SimulationSummaryPanel({
  name,
  orderValue,
  result,
}: {
  name: string;
  orderValue: number;
  result: SimulationResult;
}) {
  const accent: "red" | "green" =
    parseFloat(result.margin) < 0 ? "red" : "green";
  return (
    <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5 shadow-sm">
      {/* lewa: nazwa symulacji + kwota zlecenia */}
      <div className="flex flex-col justify-center min-w-0 lg:w-52 lg:shrink-0">
        <div className="text-[11px] uppercase tracking-wider text-gray-400">
          Wartość zlecenia
        </div>
        <div className="text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-100 leading-tight">
          {formatAmount(orderValue.toFixed(2))}
          <span className="text-sm font-normal text-gray-400"> PLN</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mt-1 min-w-0">
          <FlaskConical
            size={13}
            className="text-[var(--color-primary)] shrink-0"
          />
          <span className="truncate">{name}</span>
        </div>
      </div>
      {/* prawa: ładny panel KPI (reużyte karty Card) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
        <Card label="Przychód" value={result.revenue_total} />
        <Card label="Koszty" value={result.cost_forecast} />
        <Card
          label="Marża netto"
          value={result.margin}
          accent={accent}
          highlight
        />
        <Card
          label="Marża %"
          value={result.margin_pct ?? "—"}
          plain
          accent={accent}
          highlight
        />
      </div>
    </div>
  );
}

function ResultPanel({
  result,
  employees,
}: {
  result: SimulationResult;
  employees: { employee_id: string; employee_name: string }[];
}) {
  const marginNeg = parseFloat(result.margin) < 0;
  const ratePct = (parseFloat(result.overhead_rate) * 100).toFixed(1); // efektywny udział obciążeniowy
  // Narzut wg OBCIĄŻENIA KOSZTOWEGO rozbity (S3): koszty stałe proporcjonalne + nieprojektowi proporcjonalni
  // (silnik liczy per miesiąc wg udziału obciążenia kosztowego; tu sumy za cały okres).
  const narzutFixed = parseFloat(result.overhead_basis.fixed_part) || 0;
  const narzutPeople = parseFloat(result.overhead_basis.people_part) || 0;
  const nameFor = useMemo(() => {
    const m = new Map(employees.map((e) => [e.employee_id, e.employee_name]));
    return (s: SimStaffResult) =>
      s.is_new
        ? s.label || "Nowy etat"
        : m.get(s.employee_id ?? "") || s.label || "Pracownik";
  }, [employees]);
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <Card label="Przychód" value={result.revenue_total} />
        <Card label="Koszt prognoz." value={result.cost_forecast} />
        <Card
          label="Marża"
          value={result.margin}
          accent={marginNeg ? "red" : "green"}
          highlight
        />
        <Card
          label="Marża %"
          value={result.margin_pct ?? "—"}
          plain
          accent={marginNeg ? "red" : "green"}
          highlight
        />
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 text-xs space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-500">Płace (projektowi)</span>
          <b className="tabular-nums">{formatAmount(result.salary_cost)} PLN</b>
        </div>
        {/* Narzut wg OBCIĄŻENIA KOSZTOWEGO ROZBITY (S3): proporcjonalne koszty stałe + proporcjonalni nieprojektowi.
            Udział = obciążenie zlecenia / (portfel + zlecenie), per miesiąc; tu sumy za cały okres.
            Klik ⓘ przy „Narzut" pokazuje wyliczenie miesiąc po miesiącu. */}
        <div className="flex justify-between">
          <span className="text-gray-500">
            Koszty stałe (proporcjonalne) <span className="text-gray-400">— wg obciążenia ({ratePct}% śr.)</span>
          </span>
          <b className="tabular-nums">{formatAmount(narzutFixed.toFixed(2))} PLN</b>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">
            Nieprojektowi (proporcjonalni) <span className="text-gray-400">— wg obciążenia ({ratePct}% śr.)</span>
          </span>
          <b className="tabular-nums">{formatAmount(narzutPeople.toFixed(2))} PLN</b>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Podwykonawcy + jednorazowe</span>
          <b className="tabular-nums">
            {formatAmount(result.contractor_cost)} PLN
          </b>
        </div>
        <div className="flex justify-between border-t border-gray-100 dark:border-gray-700 pt-1 mt-1">
          <span className="text-gray-700 dark:text-gray-200 font-semibold">
            Koszt razem
          </span>
          <b className="tabular-nums">
            {formatAmount(result.cost_forecast)} PLN
          </b>
        </div>
      </div>
      {result.staff.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            <Users size={16} className="text-[var(--color-primary)]" /> Koszt
            pracy wg osób (PLN/mc · % etatu)
          </h3>
          <StaffSimChart staff={result.staff} nameFor={nameFor} />
        </div>
      )}
      {result.capacity_warnings.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 p-4 rounded-xl">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">
            <AlertTriangle size={16} /> Brak wolnych mocy (
            {result.capacity_warnings.length})
          </h3>
          <ul className="flex flex-col gap-2 text-sm text-amber-900 dark:text-amber-200">
            {result.capacity_warnings.map((w, i) => (
              <li
                key={i}
                className="border-l-2 border-amber-300 dark:border-amber-700 pl-3"
              >
                <b>{w.employee_name}</b> w {MONTHS[w.month - 1]} {w.year}:
                wliczone {(w.requested * 100).toFixed(0)}%, wolne{" "}
                {(w.available * 100).toFixed(0)}% (deficyt{" "}
                {(w.deficit * 100).toFixed(0)}%).
                <span className="text-xs block text-amber-700 dark:text-amber-400 mt-0.5">
                  → {w.options.join(" lub ")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Card({
  label,
  value,
  sub,
  accent,
  plain,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: "red" | "green";
  plain?: boolean;
  highlight?: boolean;
}) {
  const color =
    accent === "red"
      ? "text-red-600 dark:text-red-400"
      : accent === "green"
        ? "text-green-600 dark:text-green-400"
        : "text-gray-900 dark:text-gray-100";
  const ring = highlight
    ? accent === "red"
      ? "ring-1 ring-red-200 dark:ring-red-900/50"
      : accent === "green"
        ? "ring-1 ring-green-200 dark:ring-green-900/50"
        : ""
    : "";
  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 ${ring}`}
    >
      <div className="text-[11px] uppercase tracking-wider text-gray-400">
        {label}
      </div>
      <div className={`text-lg font-bold tabular-nums mt-1 ${color}`}>
        {plain ? value : formatAmount(value)}
        {!plain && (
          <span className="text-xs font-normal text-gray-400"> PLN</span>
        )}
      </div>
      {sub && (
        <div className="text-[11px] text-gray-400 mt-1.5 leading-snug">
          {sub}
        </div>
      )}
    </div>
  );
}
